import { Box, Grid, HStack } from "@chakra-ui/react";
import Image from "next/image";
import TickImage from "public/shared/check-box.png";
import CiscoImg from "public/sdnSdwan/Cisco-SD-WAN.png";
import ArubaImg from "public/sdnSdwan/Aruba-SD-WAN.png";
import FortinetImg from "public/sdnSdwan/Fortinet-SD-WAN.png";
import VersaIMg from "public/sdnSdwan/Versa-SD-WAN.png";
import PaloAltoImg from "public/sdnSdwan/Paloalto-SD-WAN.png";
import { useTextVariant } from "@/theme/hooks/useSectionStyle";
import { Text } from "@/elements";
import CiscoSdn from "./ciscoSdn";
import VersaSdn from "./versaSdn";
import ArubaSdn from "./arubaSdn";
import FortinetSdn from "./fortinetSdn";
import PaloAltoSdn from "./paloAltoSdn";

type FeatureProps = {
  label: string;
  text: string;
};

const FeaturesDisplay = ({ features }: { features: FeatureProps[] }) => {
  const TextVariant = useTextVariant();
  return (
    <Box>
      <Text variant={TextVariant} fontWeight={700} mb={4}>
        Key Features
      </Text>
      <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={6} maxW="5xl" mx="auto">
        {features.map((feature, index) => (
          <Box key={index} p={6} borderRadius="lg" bg="gray.100">
            <HStack align="start" gap={3}>
              <Image
                alt={"Point"}
                src={TickImage}
                style={{ height: "auto", width: "20px", paddingTop: "4px" }}
              />
              <Box>
                <Text variant={TextVariant} fontWeight={700}>
                  {feature.label}
                </Text>
                <Text variant={TextVariant} color="text.100">
                  {feature.text}
                </Text>
              </Box>{" "}
            </HStack>
          </Box>
        ))}
      </Grid>
    </Box>
  );
};

export const VENDORS_CONSTANTS = {
  title: "Explore Our SD-WAN Vendor Solutions ",
  desc: "OneMind delivers industry-leading SD-WAN capabilities by partnering with top networking providers. Our certified team helps you deploy, manage, and optimize software-defined networks built on the platforms you trust. ",
};

export const CISCO_CONSTANTS = {
  title: "Cloud-Delivered Networking with Enterprise-Grade Security ",
  description:
    "Cisco SD-WAN offers a cloud-delivered architecture that provides secure, scalable, and cost-effective connectivity for enterprises. ",
  children: (
    <FeaturesDisplay
      features={[
        {
          label: "Cloud OnRamp",
          text: "Seamless integration with cloud services for optimized application performance.",
        },
        {
          label: "Integrated Security",
          text: "Built-in security features including firewall, intrusion prevention, and URL filtering.",
        },
        {
          label: "Centralized Management",
          text: "Simplified operations with a single dashboard for configuration and monitoring.",
        },
        {
          label: "Application-Aware Routing",
          text: "Intelligent path selection based on application requirements.",
        },
      ]}
    />
  ),
  image: CiscoImg,
};

export const VERSA_CONSTANTS = {
  title: "Comprehensive Networking and Security Solution",
  description:
    "Versa Secure SD-WAN combines advanced networking and security functionalities into a single software platform, enabling agility and scalability.",
  children: (
    <FeaturesDisplay
      features={[
        {
          label: "Integrated Security",
          text: "Includes NGFW, UTM, and secure web gateway capabilities.",
        },
        {
          label: "Multi-Tenant Architecture",
          text: "Supports multiple customers with isolated configurations.",
        },
        {
          label: "Analytics and Visibility",
          text: "Real-time insights into network performance and security events.",
        },
        {
          label: "Flexible Deployment",
          text: "Available on-premises, in the cloud, or as a managed service.",
        },
      ]}
    />
  ),
  image: VersaIMg,
};

export const ARUBA_CONSTANTS = {
  title: "Unified WAN Edge Platform for Cloud-First Enterprises",
  description:
    "Aruba EdgeConnect SD-WAN delivers a unified platform that ensures consistent application performance and security across the WAN.",
  children: (
    <FeaturesDisplay
      features={[
        {
          label: "Business Intent Overlays",
          text: "Aligns network resources with business priorities.",
        },
        {
          label: "First-Packet iQ",
          text: "Identifies applications on the first packet for optimal routing.",
        },
        {
          label: "WAN Optimization",
          text: "Enhances application performance over any transport.",
        },
        {
          label: "Centralized Orchestration",
          text: "Simplifies management with a single pane of glass.",
        },
      ]}
    />
  ),
  image: ArubaImg,
};

export const FORTINET_CONSTANTS = {
  title: "Integrated Networking and Security for Distributed Enterprises",
  description:
    "Fortinet Secure SD-WAN combines advanced routing, WAN optimization, and integrated security to deliver a comprehensive solution.",
  children: (
    <FeaturesDisplay
      features={[
        {
          label: "NGFW Integration",
          text: "Combines SD-WAN with next-generation firewall capabilities.",
        },
        {
          label: "Application Awareness",
          text: "Identifies and prioritizes applications for optimal performance.",
        },
        {
          label: "Zero-Touch Deployment",
          text: "Simplifies branch deployments with automated provisioning.",
        },
        {
          label: "Centralized Management",
          text: "Unified console for network and security operations.",
        },
      ]}
    />
  ),
  image: FortinetImg,
  imageHeight: "480px",
};

export const PALO_ALTO_CONSTANTS = {
  title: "Cloud-Delivered SD-WAN with Integrated Security",
  description:
    "Prisma SD-WAN by Palo Alto Networks offers a cloud-native solution that ensures secure and reliable connectivity for all users and applications.",
  children: (
    <FeaturesDisplay
      features={[
        {
          label: "App-ID Technology",
          text: "Identifies applications for precise traffic control.",
        },
        {
          label: "Path Selection",
          text: "Dynamically chooses the best path for application traffic.",
        },
        {
          label: "Integrated Security",
          text: "Seamless integration with Palo Alto Networks' security services.",
        },
        {
          label: "Centralized Management",
          text: "Simplifies operations with a unified management platform.",
        },
      ]}
    />
  ),
  image: PaloAltoImg,
};

export const VENDORS_TABS = {
  CISCO: "Cisco SD-WAN ",
  VERSA: "Versa Secure SD-WAN ",
  ARUBA: "Aruba EdgeConnect SD-WAN ",
  FORTINET: "Fortinet Secure SD-WAN ",
  PALOALTO: "Palo Alto Networks Prisma SD-WAN ",
};

export const VENDORS_LIST = [
  {
    label: "Cisco SD-WAN",
    key: "cisco",
    comp: CiscoSdn,
  },
  {
    label: "Versa Secure SD-WAN",
    key: "versa",
    comp: VersaSdn,
  },
  {
    label: "Aruba Edge Connect SD-WAN",
    key: "aruba",
    comp: ArubaSdn,
  },
  {
    label: "Fortinet Secure SD-WAN",
    key: "fortinet",
    comp: FortinetSdn,
  },
  {
    label: "Palo Alto Networks Prisma SD-WAN",
    key: "paloAlto",
    comp: PaloAltoSdn,
  },
];
